import { useState, useCallback } from 'react'

export interface BillingError {
  message: string
  code: string
  limitType: string
}

/**
 * Hook to detect and manage 402 billing errors from API calls.
 * Returns a handler that catches billing errors and state to render UpgradePrompt.
 *
 * Usage:
 *   const { billingError, handleError, dismissBilling } = useBillingError()
 *
 *   // In catch blocks:
 *   catch (err) {
 *     if (!handleError(err)) {
 *       setError('generic error message')
 *     }
 *   }
 *
 *   // In JSX:
 *   {billingError && <UpgradePrompt message={billingError.message} code={billingError.code} limitType={billingError.limitType} onDismiss={dismissBilling} />}
 */
export function useBillingError() {
  const [billingError, setBillingError] = useState<BillingError | null>(null)

  const handleError = useCallback((err: unknown): boolean => {
    if (err instanceof Error) {
      const augmented = err as Error & { code?: string; limitType?: string }
      if (augmented.code === 'limit_exceeded' || augmented.code === 'upgrade_required' || augmented.code === 'subscription_required') {
        setBillingError({
          message: augmented.message,
          code: augmented.code,
          limitType: augmented.limitType || '',
        })
        return true
      }
    }
    return false
  }, [])

  const dismissBilling = useCallback(() => {
    setBillingError(null)
  }, [])

  return { billingError, handleError, dismissBilling }
}
